import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type AltiPage = 'edit' | 'backtest' | 'optimize' | 'risk' | 'list';

export interface AltiMessage {
  from: 'user' | 'bot';
  text?: string;
  html?: string;
}

export interface AltiSuggestion {
  text: string;
}

const SUGGESTIONS: Record<AltiPage, AltiSuggestion[]> = {
  list: [
    { text: 'Which portfolio improved Sharpe the most this quarter?' },
    { text: 'Flag portfolios with concentrated sector bets.' },
    { text: 'Draft a note for the review committee.' },
  ],
  edit: [
    { text: 'Suggest a benchmark for this universe.' },
    { text: 'Tag assets by sector & region.' },
    { text: 'Is my weighting scheme right for this objective?' },
  ],
  backtest: [
    { text: 'Summarize the last 12 months of performance.' },
    { text: 'Biggest contributors and detractors?' },
    { text: 'Draft an executive narrative for this result.' },
  ],
  optimize: [
    { text: 'Lower my expected volatility without losing Sharpe.' },
    { text: 'What is driving the large NVDA allocation?' },
    { text: 'Propose a tilt based on recent momentum.' },
  ],
  risk: [
    { text: "What are today's hot spots in my risk budget?" },
    { text: 'Which constituents dominate Marginal Risk?' },
    { text: 'Stress this portfolio against a rate shock.' },
  ],
};

export interface AltiContext {
  page: AltiPage;
  topAsset?: { ticker: string; contribution: number; weight: number };
  sharpe?: number;
  vol?: number;
  expectedReturn?: number;
  totalReturn?: number;
  maxDrawdown?: number;
}

@Injectable({ providedIn: 'root' })
export class ExperienceAltiService {
  private askSubject = new Subject<{ page: AltiPage; query: string }>();
  readonly ask$ = this.askSubject.asObservable();

  suggestionsFor(page: AltiPage): AltiSuggestion[] {
    return SUGGESTIONS[page] ?? [];
  }

  ask(page: AltiPage, query: string): void {
    this.askSubject.next({ page, query });
  }

  respond(page: AltiPage, query: string, ctx: AltiContext): AltiMessage {
    const q = query.toLowerCase();
    const fmtPct = (v?: number, d = 1) =>
      v === undefined || v === null || !isFinite(v) ? '—' : `${(v * 100).toFixed(d)}%`;
    const fmt = (v?: number, d = 2) =>
      v === undefined || v === null || !isFinite(v) ? '—' : v.toFixed(d);

    if (page === 'optimize' || q.includes('optim')) {
      if (q.includes('lower') || q.includes('vol')) {
        return {
          from: 'bot',
          html:
            `<p>I can rebalance toward a lower-volatility blend. Current expected vol is <b>${fmtPct(ctx.vol)}</b> ` +
            `and Sharpe is <b>${fmt(ctx.sharpe)}</b>.</p>` +
            `<p>Proposal: reduce the largest position by ~3pp and redistribute to low-beta constituents. ` +
            `Estimated Sharpe impact: <b>minor</b>.</p>`,
        };
      }
      if (q.includes('nvda') || q.includes('largest') || q.includes('top')) {
        if (ctx.topAsset) {
          return {
            from: 'bot',
            html:
              `<p><b>${ctx.topAsset.ticker}</b> is your top risk contributor at ` +
              `<b>${fmtPct(ctx.topAsset.contribution, 1)}</b> of portfolio risk, with weight <b>${fmtPct(ctx.topAsset.weight, 1)}</b>.</p>` +
              `<p>Driver: high expected return with positive correlation to other growth assets. ` +
              `Consider a partial trim or a hedge overlay.</p>`,
          };
        }
      }
      return {
        from: 'bot',
        html:
          `<p>Portfolio expected return <b>${fmtPct(ctx.expectedReturn)}</b>, vol <b>${fmtPct(ctx.vol)}</b>, Sharpe <b>${fmt(ctx.sharpe)}</b>.</p>` +
          `<p>Ask me to tilt, constrain, or re-run the optimizer.</p>`,
      };
    }

    if (page === 'risk') {
      if (ctx.topAsset) {
        return {
          from: 'bot',
          html:
            `<p>Hot spots: <b>${ctx.topAsset.ticker}</b> accounts for <b>${fmtPct(ctx.topAsset.contribution, 1)}</b> of risk. ` +
            `Diversification is concentrated at the top.</p>` +
            `<p>Suggested action: cap the top weight at 15% and re-optimize for min-variance.</p>`,
        };
      }
      return { from: 'bot', html: `<p>Risk snapshot is stable. Ask for a stress scenario or a drawdown decomposition.</p>` };
    }

    if (page === 'backtest') {
      return {
        from: 'bot',
        html:
          `<p>Total return <b>${fmtPct(ctx.totalReturn)}</b>, max drawdown <b>${fmtPct(ctx.maxDrawdown)}</b>, Sharpe <b>${fmt(ctx.sharpe)}</b>.</p>` +
          `<p>The path is steady overall; I can draft a 3-sentence narrative or compare to the benchmark.</p>`,
      };
    }

    if (page === 'edit') {
      return {
        from: 'bot',
        html:
          `<p>This universe mixes equities and ETFs. A sector-neutral benchmark usually fits best ` +
          `(e.g. MSCI World if you're global).</p>` +
          `<p>Once you save, head to <b>Optimize</b> to generate implied returns and efficient blends.</p>`,
      };
    }

    return {
      from: 'bot',
      html:
        `<p>I can summarize performance, propose tilts, or draft notes. Try one of the chips ` +
        `or ask me anything about this portfolio.</p>`,
    };
  }
}
